import { useFriendLeaderboard } from "@/hooks/useSocialRetention";
import { Trophy, Loader2, Users } from "lucide-react";
import { cn } from "@/lib/utils";

const RANK_TONES = ["text-yellow-400", "text-slate-300", "text-amber-600"];

export function FriendLeaderboard({ windowDays = 7 }: { windowDays?: number }) {
  const { rows, loading } = useFriendLeaderboard(windowDays);

  if (loading) {
    return (
      <div className="glass-card p-6 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  if (rows.length <= 1) {
    return (
      <div className="glass-card p-6 text-center">
        <Users className="w-7 h-7 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Add friends to see the weekly leaderboard.</p>
      </div>
    );
  }

  return (
    <div className="glass-card p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Trophy className="w-4 h-4 text-primary" />
        <h3 className="font-heading font-bold text-foreground">Friend Leaderboard</h3>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider ml-auto">Last {windowDays}d</span>
      </div>
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div
            key={r.user_id}
            className={cn(
              "flex items-center gap-3 p-2.5 rounded-lg transition",
              r.is_self ? "bg-primary/10 border border-primary/30" : "bg-muted/20 hover:bg-muted/30"
            )}
          >
            <span className={cn("w-6 text-center font-heading font-bold text-sm", RANK_TONES[i] || "text-muted-foreground")}>
              {i + 1}
            </span>
            <div className="w-8 h-8 rounded-lg bg-card flex items-center justify-center text-xs font-bold text-foreground shrink-0">
              {(r.display_name || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-foreground truncate">
                  {r.is_self ? "You" : (r.display_name || "Anonymous")}
                </p>
                {r.is_household && !r.is_self && (
                  <span className="text-[9px] uppercase tracking-wider text-accent border border-accent/30 px-1 rounded">Family</span>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">
                {r.lessons_completed} lessons · {r.challenge_wins} wins
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-heading font-bold text-foreground">{r.xp_gained.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">XP</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
